import { ClaimProposalDTO, ClaimProposalDTOSourceEnum, TenantDTO } from '@reposit/api-client';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import Button from '../../../components/Button';
import IdentifierValue from '../../../components/IdentifierValue';
import Modal from '../../../components/Modal';
import { Header3, P2 } from '../../../components/Typography';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import {
  REMOVE_TENANT_ON_REPOSIT_STORE_KEY,
  removeTenantOnRepositRequested,
  setCurrentRepositActionModal,
} from '../../../redux/reposit/reposit.actions';

type TenantSummary = Pick<TenantDTO, 'id' | 'firstName' | 'lastName' | 'email' | 'accepted' | 'isTenantSwap'>;

interface RemoveTenantModalProps {
  tenant: TenantSummary;
  tenants: TenantSummary[];
  tenancyId: string;
  repositId: string;
  propertyAddress: string;
  claim?: { proposals: Pick<ClaimProposalDTO, 'source' | 'autoResponded'>[] };
}

const DeletionWarning = styled.div`
  align-items: flex-start;
  background: #fff8e8;
  border: 1px solid #f5a623;
  border-radius: 8px;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  gap: 10px;
  line-height: 1.5;
  margin-bottom: 16px;
  padding: 12px 14px;

  svg {
    color: #b87a00;
    flex-shrink: 0;
  }
`;

const SummaryCard = styled.div`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  padding: 22px 14px 14px;
  position: relative;
`;

const CardTypePill = styled.span`
  background: ${(props) => props.theme.colors.bgHoverAccent};
  border-bottom-right-radius: 4px;
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 0;
`;

type SectionChange = 'deleted' | 'modified' | 'unmodified';

const SectionDivider = styled.div<{ $change: SectionChange }>`
  align-items: center;
  display: flex;
  gap: 10px;
  margin: 18px 0 12px;

  &::before,
  &::after {
    background: ${(props) =>
      props.$change === 'deleted'
        ? props.theme.colors.negative
        : props.$change === 'modified'
          ? '#F5A623'
          : props.theme.colors.tertiaryBrand};
    content: '';
    flex: 1;
    height: 1px;
    opacity: 0.4;
  }
`;

const SectionDividerText = styled.span<{ $change: SectionChange }>`
  color: ${(props) =>
    props.$change === 'deleted'
      ? props.theme.colors.negative
      : props.$change === 'modified'
        ? '#B87A00'
        : props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SummaryTopRow = styled.div`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
`;

const SummaryName = styled.div<{ deleted?: boolean }>`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  opacity: ${(props) => (props.deleted ? 0.5 : 1)};
  overflow-wrap: anywhere;
  text-decoration: ${(props) => (props.deleted ? 'line-through' : 'none')};
`;

const SummaryDetail = styled.div`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 12px;
  margin-top: 4px;
  overflow-wrap: anywhere;
`;

const TenantCount = styled.div`
  align-items: center;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  gap: 8px;
`;

const OldCount = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-weight: 400;
  text-decoration: line-through;
`;

const ChangeArrow = styled.span`
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-weight: 400;
`;

const RemainingTenants = styled.ul`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    margin-bottom: 0;
  }
`;

const ActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;

  button {
    margin: 0;
  }
`;

const getTenantName = (tenant: TenantSummary) => [tenant.firstName, tenant.lastName].filter(Boolean).join(' ') || tenant.email;
const isOriginalTenant = (tenant: TenantSummary) => tenant.accepted && !tenant.isTenantSwap;
const formatTenantCount = (count: number) => `${count} ${count === 1 ? 'tenant' : 'tenants'}`;
const isSubmittingSelector = createLoadingSelector([REMOVE_TENANT_ON_REPOSIT_STORE_KEY]);

export const RemoveTenantModal: React.FC<RemoveTenantModalProps> = ({
  tenant,
  tenants,
  tenancyId,
  repositId,
  propertyAddress,
  claim,
}) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(isSubmittingSelector);
  const remainingTenants = tenants.filter(({ id }) => id !== tenant.id);
  const isLastOriginalTenant = isOriginalTenant(tenant) && !remainingTenants.some(isOriginalTenant);
  const isAutoRespondedClaim = claim
    ? claim.proposals.some(({ source, autoResponded }) => source === ClaimProposalDTOSourceEnum.SUPPLIER && autoResponded)
    : false;
  const warnings = [
    ...(isLastOriginalTenant ? ['You cannot delete the last remaining original tenant from this Reposit.'] : []),
    ...(claim
      ? [
          isAutoRespondedClaim
            ? 'You are deleting this tenant from an auto-responded claim.'
            : 'You are deleting this tenant during the claim phase.',
        ]
      : []),
  ];
  const dismiss = () => {
    if (!isSubmitting) dispatch(setCurrentRepositActionModal({ name: '' }));
  };
  const remove = () => {
    if (!isSubmitting && !isLastOriginalTenant) dispatch(removeTenantOnRepositRequested({ tenancyId, tenantId: tenant.id }));
  };

  return (
    <Modal onDismiss={isSubmitting ? undefined : dismiss} scrollable capHeight>
      <div role="dialog" aria-modal="true" aria-labelledby="remove-tenant-title">
        <Header3 id="remove-tenant-title" style={{ marginBottom: 4 }}>
          Remove Tenant
        </Header3>
        <P2 style={{ marginBottom: 20 }}>Review the changes before removing this tenant.</P2>

        {warnings.map((warning) => (
          <DeletionWarning key={warning} role="alert">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <path d="M12 3 2 21h20L12 3Z" />
              <path d="M12 9v5" />
              <circle cx="12" cy="17" r="0.5" fill="currentColor" />
            </svg>
            <span>{warning}</span>
          </DeletionWarning>
        ))}

        <SectionDivider $change="deleted">
          <SectionDividerText $change="deleted">Deleted</SectionDividerText>
        </SectionDivider>
        <SummaryCard>
          <CardTypePill>Tenant</CardTypePill>
          <SummaryName deleted>{getTenantName(tenant)}</SummaryName>
          <SummaryDetail>{tenant.email}</SummaryDetail>
          <SummaryDetail>{tenant.accepted ? 'Accepted tenant' : 'Pending invite'}</SummaryDetail>
        </SummaryCard>

        <SectionDivider $change="modified">
          <SectionDividerText $change="modified">Modified</SectionDividerText>
        </SectionDivider>
        <SummaryCard>
          <CardTypePill>Reposit</CardTypePill>
          <SummaryTopRow>
            <div>
              <SummaryName>{propertyAddress}</SummaryName>
              <SummaryDetail>
                <IdentifierValue>{repositId}</IdentifierValue>
              </SummaryDetail>
            </div>
            <TenantCount aria-label={`Tenant count changes from ${tenants.length} to ${remainingTenants.length}`}>
              <OldCount>{formatTenantCount(tenants.length)}</OldCount>
              <ChangeArrow aria-hidden="true">→</ChangeArrow>
              <span>{formatTenantCount(remainingTenants.length)}</span>
            </TenantCount>
          </SummaryTopRow>
          {!remainingTenants.length && <SummaryDetail>No tenants remaining</SummaryDetail>}
        </SummaryCard>

        {remainingTenants.length > 0 && (
          <>
            <SectionDivider $change="unmodified">
              <SectionDividerText $change="unmodified">Unmodified</SectionDividerText>
            </SectionDivider>
            <RemainingTenants aria-label="Remaining tenants">
              {remainingTenants.map((remainingTenant) => (
                <SummaryCard as="li" key={remainingTenant.id}>
                  <CardTypePill>Tenant</CardTypePill>
                  <SummaryName>{getTenantName(remainingTenant)}</SummaryName>
                  <SummaryDetail>{remainingTenant.email}</SummaryDetail>
                  <SummaryDetail>{remainingTenant.accepted ? 'Accepted tenant' : 'Pending invite'}</SummaryDetail>
                </SummaryCard>
              ))}
            </RemainingTenants>
          </>
        )}

        <ActionBar>
          <Button buttonType="secondary" type="button" onClick={dismiss} disabled={isSubmitting} noArrow>
            Go back
          </Button>
          <Button buttonType="warning" type="button" onClick={remove} disabled={isSubmitting || isLastOriginalTenant} noArrow>
            {isSubmitting ? 'Removing tenant...' : 'Remove tenant'}
          </Button>
        </ActionBar>
      </div>
    </Modal>
  );
};
